import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  listAdminChannelConnections,
  listAdminInventoryConnections,
  listAdminOrganizations,
  proxyAdminChannelProvider,
  proxyAdminInventoryProvider,
} from '../../admin/adminApi'
import type { AdminConnectionSummary, AdminOrganizationSummary, AdminProviderProxyMethod, AdminProviderProxyResponse, AdminProviderProxySurface } from '../../admin/types'
import { Button, Card, Field, I, Pill, gridColumns } from '../../components/ui'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport'
import { CodeValue, EmptyState, ErrorState, LoadingState } from './adminPageHelpers'
import { tableContainerStyle, tdStyle, thStyle } from './adminPageFormatters'

interface AdminProviderProxyPageProps {
  accessToken: string
  initialOrganizationId?: number | null
  initialConnectionId?: number | null
  initialSurface?: AdminProviderProxySurface | null
}

const METHODS: AdminProviderProxyMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']
const MUTATING_METHODS = new Set<AdminProviderProxyMethod>(['POST', 'PUT', 'PATCH', 'DELETE'])
const FORBIDDEN_HEADERS = new Set([
  'authorization', 'proxy-authorization', 'cookie', 'set-cookie', 'x-api-key', 'api-key', 'x-auth-token',
  'host', 'content-length', 'transfer-encoding', 'connection', 'upgrade', 'forwarded', 'x-forwarded-for', 'x-forwarded-host', 'x-forwarded-proto',
])

export function AdminProviderProxyPage({ accessToken, initialOrganizationId = null, initialConnectionId = null, initialSurface = null }: AdminProviderProxyPageProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [organizations, setOrganizations] = useState<AdminOrganizationSummary[]>([])
  const [connections, setConnections] = useState<AdminConnectionSummary[]>([])
  const [surface, setSurface] = useState<AdminProviderProxySurface>(initialSurface ?? 'channel')
  const [organizationId, setOrganizationId] = useState(initialOrganizationId ? String(initialOrganizationId) : '')
  const [connectionId, setConnectionId] = useState(initialConnectionId ? String(initialConnectionId) : '')
  const [method, setMethod] = useState<AdminProviderProxyMethod>('GET')
  const [path, setPath] = useState('/')
  const [queryParamsText, setQueryParamsText] = useState('')
  const [headersText, setHeadersText] = useState('')
  const [body, setBody] = useState('')
  const [response, setResponse] = useState<AdminProviderProxyResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [connectionsLoading, setConnectionsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const loadOrganizations = useCallback(function loadOrganizations() {
    setLoading(true)
    setError(false)
    listAdminOrganizations(accessToken)
      .then(orgs => {
        setOrganizations(orgs)
        setOrganizationId(current => current || (orgs[0] ? String(orgs[0].id) : ''))
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [accessToken])

  useEffect(() => {
    void Promise.resolve().then(loadOrganizations)
  }, [loadOrganizations])

  useEffect(() => {
    void Promise.resolve().then(() => {
      if (!organizationId) {
        setConnections([])
        setConnectionId('')
        return
      }
      setConnectionsLoading(true)
      setResponse(null)
      const organization = Number(organizationId)
      const request = surface === 'channel' ? listAdminChannelConnections(accessToken, organization) : listAdminInventoryConnections(accessToken, organization)
      request
        .then(rows => {
          setConnections(rows)
          setConnectionId(current => {
            if (initialConnectionId && rows.some(row => row.id === initialConnectionId)) return String(initialConnectionId)
            if (rows.some(row => String(row.id) === current)) return current
            return rows[0] ? String(rows[0].id) : ''
          })
        })
        .catch(() => setConnections([]))
        .finally(() => setConnectionsLoading(false))
    })
  }, [accessToken, initialConnectionId, organizationId, surface])

  const selectedConnection = useMemo(() => connections.find(connection => String(connection.id) === connectionId) ?? null, [connections, connectionId])

  function parseLines(value: string): Record<string, string> {
    const output: Record<string, string> = {}
    value.split('\n').forEach(rawLine => {
      const line = rawLine.trim()
      if (!line) return
      const separatorIndex = line.includes('=') ? line.indexOf('=') : line.indexOf(':')
      if (separatorIndex < 0) throw new Error('BAD_LINE')
      const key = line.slice(0, separatorIndex).trim()
      const lineValue = line.slice(separatorIndex + 1).trim()
      if (!key) throw new Error('BAD_LINE')
      output[key] = lineValue
    })
    return output
  }

  function validate(): { queryParams: Record<string, string>; headers: Record<string, string> } | null {
    const trimmedPath = path.trim()
    if (!trimmedPath.startsWith('/')) {
      setValidationError(t('admin.providerProxy.validation.pathSlash'))
      return null
    }
    if (trimmedPath.startsWith('//') || trimmedPath.includes('\\') || trimmedPath.includes('://') || /%3a/i.test(trimmedPath) || /%2f/i.test(trimmedPath)) {
      setValidationError(t('admin.providerProxy.validation.pathAbsolute'))
      return null
    }
    if (trimmedPath.split('/').includes('..')) {
      setValidationError(t('admin.providerProxy.validation.pathTraversal'))
      return null
    }
    if (!connectionId) {
      setValidationError(t('admin.providerProxy.validation.connection'))
      return null
    }

    try {
      const queryParams = parseLines(queryParamsText)
      const headers = parseLines(headersText)
      const forbidden = Object.keys(headers).find(key => FORBIDDEN_HEADERS.has(key.toLowerCase()))
      if (forbidden) {
        setValidationError(t('admin.providerProxy.validation.forbiddenHeader', { header: forbidden }))
        return null
      }
      setValidationError(null)
      return { queryParams, headers }
    } catch {
      setValidationError(t('admin.providerProxy.validation.keyValue'))
      return null
    }
  }

  async function submit() {
    const parsed = validate()
    if (!parsed) return
    if (MUTATING_METHODS.has(method) && !window.confirm(t('admin.providerProxy.confirmMutating', { method }))) return

    setSubmitting(true)
    setResponse(null)
    try {
      const request = {
        method,
        path: path.trim(),
        queryParams: parsed.queryParams,
        headers: parsed.headers,
        body: body.trim() ? body : null,
      }
      const result = surface === 'channel'
        ? await proxyAdminChannelProvider(accessToken, Number(connectionId), request)
        : await proxyAdminInventoryProvider(accessToken, Number(connectionId), request)
      setResponse(result)
    } catch {
      setValidationError(t('admin.providerProxy.validation.requestFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <LoadingState label={t('admin.providerProxy.loading')} />
  if (error) return <ErrorState title={t('admin.providerProxy.error.title')} body={t('admin.providerProxy.error.body')} onRetry={loadOrganizations} />

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(3, minmax(0, 1fr))'), gap: 12 }}>
          <label style={{ display: 'block' }}>
            <div style={labelStyle}>{t('admin.providerProxy.fields.surface')}</div>
            <select aria-label={t('admin.providerProxy.fields.surface')} value={surface} onChange={event => { setSurface(event.target.value as AdminProviderProxySurface); setConnectionId('') }} style={selectStyle}>
              <option value="channel">{t('admin.providerProxy.surface.channel')}</option>
              <option value="inventory">{t('admin.providerProxy.surface.inventory')}</option>
            </select>
          </label>
          <label style={{ display: 'block' }}>
            <div style={labelStyle}>{t('admin.providerProxy.fields.organization')}</div>
            <select aria-label={t('admin.providerProxy.fields.organization')} value={organizationId} onChange={event => { setOrganizationId(event.target.value); setConnectionId('') }} style={selectStyle}>
              {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
            </select>
          </label>
          <label style={{ display: 'block' }}>
            <div style={labelStyle}>{t('admin.providerProxy.fields.connection')}</div>
            <select aria-label={t('admin.providerProxy.fields.connection')} value={connectionId} onChange={event => setConnectionId(event.target.value)} style={selectStyle} disabled={connectionsLoading || connections.length === 0}>
              {connections.map(connection => <option key={connection.id} value={connection.id}>{connection.displayName} · {connection.provider} · #{connection.id}</option>)}
            </select>
          </label>
        </div>
        {selectedConnection && (
          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Pill tone="info" size="sm">{selectedConnection.provider}</Pill>
            <CodeValue>#{selectedConnection.id}</CodeValue>
            <span style={{ color: 'var(--fg-muted)', fontSize: 13 }}>{selectedConnection.organizationName}</span>
          </div>
        )}
      </Card>

      {connections.length === 0 ? (
        <EmptyState title={t('admin.providerProxy.empty.title')} body={t('admin.providerProxy.empty.body')} />
      ) : (
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, '160px minmax(0, 1fr)'), gap: 12 }}>
            <label style={{ display: 'block' }}>
              <div style={labelStyle}>{t('admin.providerProxy.fields.method')}</div>
              <select aria-label={t('admin.providerProxy.fields.method')} value={method} onChange={event => setMethod(event.target.value as AdminProviderProxyMethod)} style={selectStyle}>
                {METHODS.map(item => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <Field label={t('admin.providerProxy.fields.path')} placeholder={t('admin.providerProxy.placeholders.path')} value={path} onChange={event => setPath(event.target.value)} dir="ltr" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) minmax(0, 1fr)'), gap: 12, marginTop: 12 }}>
            <TextArea label={t('admin.providerProxy.fields.queryParams')} hint={t('admin.providerProxy.hints.keyValue')} value={queryParamsText} onChange={setQueryParamsText} />
            <TextArea label={t('admin.providerProxy.fields.headers')} hint={t('admin.providerProxy.hints.headers')} value={headersText} onChange={setHeadersText} />
          </div>
          <div style={{ marginTop: 12 }}>
            <TextArea label={t('admin.providerProxy.fields.body')} hint={t('admin.providerProxy.hints.body')} value={body} onChange={setBody} rows={8} />
          </div>
          {validationError && <div role="alert" style={{ marginTop: 12, color: 'var(--danger)', fontWeight: 800 }}>{validationError}</div>}
          <div style={{ marginTop: 16 }}>
            <Button kind={MUTATING_METHODS.has(method) ? 'coral' : 'primary'} icon={I.zap} onClick={submit} disabled={submitting}>
              {submitting ? t('admin.providerProxy.actions.sending') : t('admin.providerProxy.actions.send')}
            </Button>
          </div>
        </Card>
      )}

      {response && (
        <Card>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 20 }}>{t('admin.providerProxy.response.title')}</h2>
            <Pill tone={response.status >= 200 && response.status < 300 ? 'success' : response.status >= 500 ? 'danger' : 'warning'} size="sm">{t('admin.providerProxy.response.status', { status: response.status })}</Pill>
          </div>
          <div style={{ ...tableContainerStyle, marginTop: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={thStyle}>{t('admin.providerProxy.response.header')}</th><th style={thStyle}>{t('admin.providerProxy.response.value')}</th></tr></thead>
              <tbody>
                {Object.entries(response.headers).length === 0 ? (
                  <tr><td style={tdStyle} colSpan={2}>{t('admin.common.none')}</td></tr>
                ) : Object.entries(response.headers).map(([key, value]) => (
                  <tr key={key}><td style={tdStyle}><CodeValue>{key}</CodeValue></td><td style={{ ...tdStyle, overflowWrap: 'anywhere' }}>{value}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <pre style={{ margin: '14px 0 0', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', background: 'var(--ink-50)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 14, fontFamily: 'var(--font-mono)', fontSize: 12, direction: 'ltr', textAlign: 'start' }}>
            {response.body ?? t('admin.providerProxy.response.emptyBody')}
          </pre>
        </Card>
      )}
    </div>
  )
}

function TextArea({ label, hint, value, onChange, rows = 4 }: { label: string; hint: string; value: string; onChange: (value: string) => void; rows?: number }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={labelStyle}>{label}</div>
      <textarea
        aria-label={label}
        value={value}
        onChange={event => onChange(event.target.value)}
        rows={rows}
        dir="ltr"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '11px 14px',
          fontSize: 13,
          fontFamily: 'var(--font-mono)',
          color: 'var(--fg)',
          background: 'white',
          border: '1px solid var(--border-default)',
          borderRadius: 10,
          outline: 'none',
          resize: 'vertical',
        }}
      />
      <div style={{ marginTop: 6, color: 'var(--fg-subtle)', fontSize: 12 }}>{hint}</div>
    </label>
  )
}

const labelStyle = { fontSize: 13, fontWeight: 700, marginBottom: 6, color: 'var(--fg)' } as const

const selectStyle = {
  width: '100%',
  padding: '11px 14px',
  fontSize: 14,
  fontFamily: 'inherit',
  color: 'var(--fg)',
  background: 'white',
  border: '1px solid var(--border-default)',
  borderRadius: 10,
  outline: 'none',
} as const
